import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { PopoverBody, Col, ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import {
  setCurrentNotification,
  loadAllNotifications,
  changeSortConfigNotification,
  searchNotifications,
  setAll,
  setClearNotificationsList,
} from 'redux/actions/notificationActions';

import useApi from 'hooks/useApi';

import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as SortByIcon } from 'assets/icon-sort-by.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as CheckRoundIcon } from 'assets/icon-check-round.svg';
import { LabelIcon } from 'shared/components/Labels';
import { ButtonLabelIcon } from 'shared/components/Buttons';
import { colors } from 'shared/defaultTheme';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import AddNotify from 'shared/components/AddNotify';
import { AddFormModal } from 'shared/components/SharedStyles/SharedStyles';
import { setNewCommunication } from 'redux/actions/clientActions';
import Table from './NotificationsTable';

import {
  NotificationWrapper,
  NotificationTableBackground,
  Popover,
  OptionMenu,
  SearchNotification,
} from '../styles';

const Notifications = ({ clientId }) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const { cusNavId, mail } = useSelector((state) => state.auth);
  const notificationState = useSelector((state) => state.notification);
  const { newCommunication } = useSelector((state) => state.clientState);
  const { sortConfig, searchValue } = notificationState;
  const [orderName, setOrderName] = useState('Created');
  const [isOrder, setIsOrder] = useState(false);

  const [openFormModal, setOpenFormModal] = useState(false);

  const [{ loading }, getNotificationsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/notifications/${cusNavId}/search?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  const getSortIcon = (fieldName) => {
    const sortOption = sortConfig.find(
      (currentSortOption) => currentSortOption.field === fieldName
    );
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const getOrderIcon = (isSort) => {
    const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc === isSort);
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const onSort = (fieldName, isSort) => {
    dispatch(changeSortConfigNotification({ fieldName, isSort }));
  };

  useEffect(() => {
    onSort(orderName, isOrder);
  }, [orderName, isOrder]);

  const notificationsData = async (searchParam = undefined) => {
    try {
      const {
        data: { errors, data },
      } = await getNotificationsData(searchParam ? { data: searchParam } : undefined);
      if (errors.length) {
        return;
      }
      const filterNotification = data.filter((x) => x.id);
      dispatch(loadAllNotifications(filterNotification));
      dispatch(setAll(filterNotification));
    } catch (ex) {
      dispatch(loadAllNotifications([]));
    }
  };

  useEffect(() => {
    return () => {
      dispatch(setAll([]));
    };
  }, []);

  const onChangeSearch = (e) => {
    if (e) {
      const { value } = e.target;
      dispatch(searchNotifications(value));
    } else {
      dispatch(searchNotifications(''));
    }
  };

  const searchNotification = () => {
    const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc !== 'none');
    const searchParam = sortOption
      ? {
          filters: [
            ...sortConfig,
            {
              model: 'Notify',
              field: 'ClientId',
              filter: [clientId],
            },
            {
              model: 'Notify',
              field: 'Name',
              filter: searchValue && searchValue.length >= 2 ? [searchValue] : null,
              isRegex: !!(searchValue && searchValue.length >= 2),
            },
          ],
          reduce: {
            limit: 0,
            skip: 0,
          },
        }
      : {
          filters: [
            {
              model: 'Notify',
              field: 'Name',
              filter: searchValue && searchValue.length >= 2 ? [searchValue] : null,
              isRegex: !!(searchValue && searchValue.length >= 2),
              asc: true,
            },
            {
              model: 'Notify',
              field: 'ClientId',
              filter: [clientId],
            },
          ],
          reduce: {
            limit: 0,
            skip: 0,
          },
        };
    notificationsData(searchParam);
  };

  useEffect(() => {
    if (clientId) {
      searchNotification();
    }
  }, [sortConfig, clientId]);

  useEffect(() => {
    if (searchValue != null && !loading) {
      searchNotification();
    }
  }, [searchValue]);

  const handleRefresh = () => {
    onSort('Created', false);
    dispatch(setClearNotificationsList(true));
  };

  useEffect(() => {
    if (newCommunication) {
      setOpenFormModal(true);
    }
  }, [newCommunication]);

  const onCloseFormModal = () => {
    setOpenFormModal(false);
    dispatch(setNewCommunication(undefined));
  };

  return (
    <NotificationWrapper>
      <NotificationTableBackground className="mr-2">
        <div className="d-flex justify-content-between">
          <Col>
            <div className="form d-flex justify-content-end">
              <SearchNotification
                Icon={() => null}
                borderRadius={2}
                className="mb-4 w-search-notification m-0 h-50"
                inputClassName="w-100"
                buttonClassName=""
                rightIcon={<SearchIcon fill={colors.black} />}
                onChange={onChangeSearch}
                value={searchValue ?? ''}
                placeholder={t('notification_search')}
              />
            </div>
          </Col>
          <div className="d-flex container-action-button">
            <ButtonLabelIcon
              text={t('general_order_by')}
              type="button"
              Icon={() => <SortByIcon fill={colors.black} />}
              className="button-icon mr-2"
              id="PopoverFocusSort"
            />
            <ButtonLabelIcon
              id="refresh-icon"
              text={t('general_button_refresh')}
              type="button"
              Icon={() => <RefreshIcon fill={colors.black} />}
              className="button-icon mr-2"
              onClick={handleRefresh}
            />

            <ButtonLabelIcon
              id="add-icon-notify"
              text={t('general_button_new')}
              type="button"
              Icon={() => <AddIcon fill={colors.black} />}
              className="button-icon"
              onClick={() => {
                dispatch(setCurrentNotification(null));
                setOpenFormModal(true);
              }}
            />
          </div>
        </div>
        <Popover trigger="legacy" placement="bottom" target="PopoverFocusSort">
          <PopoverBody>
            <OptionMenu onClick={() => setOrderName('Name')}>
              <LabelIcon
                label={t('notification_column_issue')}
                color=""
                className="ml-1 body-text--primary action-option-order-label"
                classNameMain="mb-3 label-icon-sort"
                isStyle={false}
                Icon={() => getSortIcon('Name')}
              />
            </OptionMenu>
            <OptionMenu onClick={() => setOrderName('Created')}>
              <LabelIcon
                label={t('notification_column_created_date')}
                color=""
                className="ml-1 body-text--primary action-option-order-label"
                classNameMain="mb-3 label-icon-sort"
                isStyle={false}
                Icon={() => getSortIcon('Created')}
              />
            </OptionMenu>
            <OptionMenu onClick={() => setOrderName('Date')}>
              <LabelIcon
                label={t('notification_column_start_date')}
                color=""
                className="ml-1 body-text--primary action-option-order-label"
                classNameMain="mb-3 label-icon-sort"
                isStyle={false}
                Icon={() => getSortIcon('Date')}
              />
            </OptionMenu>
            <hr />
            <OptionMenu onClick={() => setIsOrder(true)}>
              <LabelIcon
                label={t('general_ascending')}
                color=""
                className="ml-1 body-text--primary action-option-order-label"
                classNameMain="mb-3 label-icon-sort"
                isStyle={false}
                Icon={() => getOrderIcon(true)}
              />
            </OptionMenu>
            <OptionMenu onClick={() => setIsOrder(false)}>
              <LabelIcon
                label={t('general_descending')}
                color=""
                className="ml-1 body-text--primary action-option-order-label"
                classNameMain="mb-3 label-icon-sort"
                isStyle={false}
                Icon={() => getOrderIcon(false)}
              />
            </OptionMenu>
            <OptionMenu onClick={() => setIsOrder('none')}>
              <LabelIcon
                label={t('general_none')}
                color=""
                className="ml-1 body-text--primary action-option-order-label"
                classNameMain="mb-3 label-icon-sort"
                isStyle={false}
                Icon={() => getOrderIcon('none')}
              />
            </OptionMenu>
          </PopoverBody>
        </Popover>
        <>
          <Table
            handleRefresh={handleRefresh}
            notificationsData={notificationsData}
            loading={loading}
            clientId={clientId}
            setOpenFormModal={setOpenFormModal}
          />
        </>
      </NotificationTableBackground>
      <AddFormModal isOpen={openFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <div className="title-wrapper">
          <h1 className="title">{t('notification_title_add')}</h1>
        </div>
        <ModalBody>
          <AddNotify
            onCancel={onCloseFormModal}
            isNotifyClient
            notifyClientId={clientId}
            isClientsSelectorDisabled
          />
        </ModalBody>
      </AddFormModal>
    </NotificationWrapper>
  );
};

Notifications.propTypes = {
  clientId: PropTypes.string.isRequired,
};

export default Notifications;
